import { mmToPixels } from "./UTILS";

export const getBlocksSheetData = (blocks, options) => {
  const {
    blockSize,
    pixelBlockSize,
    blocksPerImageWidth,
    blocksPerImageHeight,
    paperSize,
    palette,
    removeLightestColour,
    countLightestColour,
  } = options;

  const { lengthInMM = 297, widthInMM = 210 } = paperSize;
  const sheetWidth = mmToPixels(widthInMM);
  const sheetHeight = mmToPixels(lengthInMM);

  const sheetInfo = createEmptySheetData({
    blockSize,
    pixelBlockSize,
    sheetWidth,
    sheetHeight,
    blocksPerImageWidth,
    blocksPerImageHeight,
  });

  let sheetData = { ...sheetInfo };
  const totalBlocks = blocks.length;

  for (let i = 0; i < totalBlocks; i++) {
    const col = i % blocksPerImageWidth;
    const row = (i - col) / blocksPerImageWidth;

    // find a sheet for this block
    const block = blocks[i];
    const sheet = sheetData.sheets.find(
      (s) =>
        col >= s.firstColIndex &&
        col <= s.lastColIndex &&
        row >= s.firstRowIndex &&
        row <= s.lastRowIndex
    );

    // add to the sheets block data if it already has block data
    // or start the sheets blocks array if it doesn't have any yet.
    const excludeBlock = removeLightestColour && block === 0;

    if (!excludeBlock) {
      sheet.blocks.push(block);
    }
  }

  let blocksToCount = blocks;
  // filter out zero blocks if removing lightest colour
  if (removeLightestColour || !countLightestColour) {
    blocksToCount = [...blocks].filter((b) => b > 0);
  }

  sheetData.global.blockCounts = countBlockTypes(blocksToCount);

  const blockCountsPerSheet = getSheetBlockTypeCounts(
    sheetData.sheets,
    palette
  );

  sheetData.global.blockCountsPerSheet = blockCountsPerSheet;

  return sheetData;
};

//
// COUNT BLOCK TYPES
//
const countBlockTypes = (blocks) => {
  return blocks.reduce((blks, bType) => {
    if (bType > 0) {
      blks.total = ++blks.total || 1;
    }
    blks[bType] = ++blks[bType] || 1;
    return blks;
  }, {});
};

const getSheetBlockTypeCounts = (sheets, palette) => {
  const data = [];

  const { labels } = palette;

  sheets.forEach((sheet, i) => {
    const sheetData = { sheetNumber: i + 1, blockCounts: {} };

    sheet.blocks.forEach((block) => {
      const blockLabel = labels[block];

      if (blockLabel > 1) {
        if (sheetData.blockCounts[blockLabel]) {
          sheetData.blockCounts[blockLabel]++;
        } else {
          sheetData.blockCounts[blockLabel] = 1;
        }
      }
    });

    data.push(sheetData);
  });

  return data;
};

//
// SHEETS
//
export const createEmptySheetData = ({
  blockSize,
  sheetWidth,
  sheetHeight,
  pixelBlockSize,
  blocksPerImageWidth,
  blocksPerImageHeight,
}) => {
  /**
   * Calculate sheet data to show user and to determine which blocks
   * go into each sheet.
   */
  const blocksPerSheetWidth = Math.floor(sheetWidth / blockSize);
  const blocksPerSheetHeight = Math.floor(sheetHeight / blockSize);
  const sheetsWide = Math.ceil(blocksPerImageWidth / blocksPerSheetWidth);
  const sheetsHigh = Math.ceil(blocksPerImageHeight / blocksPerSheetHeight);
  const totalSheets = sheetsWide * sheetsHigh;
  const blocksPerSheet = blocksPerSheetWidth * blocksPerSheetHeight;
  const sheets = [];

  for (let x = 0; x < sheetsHigh; x++) {
    // for each sheet down
    for (let y = 0; y < sheetsWide; y++) {
      // for each sheet along
      const firstColIndex = y * blocksPerSheetWidth;
      const firstRowIndex = x * blocksPerSheetHeight;

      // ensure the last col index is restricted to the image size
      const lastColIndex = Math.min(
        firstColIndex + blocksPerSheetWidth - 1,
        blocksPerImageWidth - 1
      );
      const lastRowIndex = Math.min(
        firstRowIndex + blocksPerSheetHeight - 1,
        blocksPerImageHeight - 1
      );

      // if last col index is greating than the blocks per image height do something
      sheets.push({
        sheetPosition: { x, y },
        firstColIndex,
        firstRowIndex,
        lastColIndex,
        lastRowIndex,
        blocks: [],
      });
    }
  }

  const emptySheetData = {
    sheets,
    global: {
      sheetWidth,
      sheetHeight,
      blocksPerSheet,
      totalSheets,
      sheetsWide,
      sheetsHigh,
      blockSize,
      pixelBlockSize,
      blocksPerSheetWidth,
      blocksPerSheetHeight,
      blocksPerImageWidth,
      blocksPerImageHeight,
    },
  };

  return emptySheetData;
};
